import { useFormik, FormikErrors, FormikTouched } from 'formik';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, Flex, Label, Radio, Spinner, Text, useColorMode } from 'theme-ui';
import * as Yup from 'yup';
import moment from 'moment';
import { RootState, StepOneFormValues, usersProps, IContract } from '../../../../../@types';
import theme from '../../../../theme';
import { Inputform } from '../../../forms-component/InputForm';
import FormSelect from '../../../forms-component/globalSelect';
import MultiCard from './Multicard';
import FolderNumberDropdown from './folderNumberDropdown';
import { State, City, IState, ICity } from 'country-state-city';
import { getAllUserWithoutPagination } from '../../../../store/actions/user.action';
import { createStepOne, editProjectById } from '../../../../store/actions/project.action';
import { getAllClients } from '../../../../store/actions/masterData.action';
import { uploadMedia } from '../../../../store/api';

function isSalesRepObject(rep: unknown): rep is { _id: string } {
  return typeof rep === 'object' && rep !== null && '_id' in rep;
}

const StepOne: React.FC = () => {
  const dispatch = useDispatch();
  const { auther } = useSelector((state: RootState) => state.auth);
  const { createprojectMasterData, getproject, loader } = useSelector((state: RootState) => state.project);
  const { userData } = useSelector((state: RootState) => state.user);
  const { clientList } = useSelector((state: RootState) => state.masterData);
  const salesReps = (userData?.data || []).filter((user: usersProps) =>
    user?.departments?.some((dept: { name: string }) => dept?.name === 'SALES'));

  const navigate = useNavigate();
  const location = useLocation();
  const parsed = queryString.parse(location.search);
  const projectId = typeof parsed.id === 'string' ? parsed.id : '';
  const architects = createprojectMasterData?.allArchitect?.data || [];
  const projectManagers = createprojectMasterData?.allProjectManagers || [];
  const projectWorkers = createprojectMasterData?.allProjectWorkers || [];
  const [colorMode] = useColorMode();

  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [emailOptions, setEmailOptions] = useState<{ label: string; value: string; clientName?: string; clientId?: string }[]>([]);
  const [isManualClient, setIsManualClient] = useState<boolean>(false);

  const [uploadingRowIndex, setUploadingRowIndex] = useState<number | null>(null);
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});
  const [contractError, setContractError] = useState<string | null>(null);

  useEffect(() => {
    const clientEmailOptions = (clientList?.data || []).map((c: { _id?: string; name?: string; email?: string }) => ({
      label: `${c.email || ''}`.trim(),
      value: c.email || '',
      clientName: c.name || '',
      clientId: c._id || ''
    }));

    setEmailOptions(clientEmailOptions);
    if (getproject?.clientEmail) {
      const emailStr = Array.isArray(getproject?.clientEmail) ? getproject?.clientEmail[0] : getproject?.clientEmail;
      if (emailStr && !clientEmailOptions.some((opt: { value: string }) => opt.value === emailStr)) {
        setIsManualClient(true);
      }
    }
  }, [clientList, getproject]);

  const formik = useFormik<StepOneFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: getproject?.name,
      architect: getproject?.architect || null,
      projectManager: getproject?.projectManager || null,
      // projectWorker: getproject?.projectWorker || null,
      blueprint: getproject?.blueprint,
      quoteNumber: getproject?.quoteNumber,
      folderNumber: getproject?.folderNumber,
      clientAddress: getproject?.clientAddress,
      salesRepresentative: isSalesRepObject(getproject?.salesRepresentative)
        ? getproject.salesRepresentative._id
        : typeof getproject?.salesRepresentative === 'string'
          ? getproject.salesRepresentative
          : '',
      clientContactPerson: getproject?.clientContactPerson,
      clientEmail: Array.isArray(getproject?.clientEmail) ? (getproject?.clientEmail[0] || '') : (getproject?.clientEmail || ''),
      clientEmailId: (getproject as any)?.clientEmailId || '',
      siteVisitPin: getproject?.siteVisitPin || '',
      tmQuote: getproject?.tmQuote || false,
      createdBy: auther?._id,
      stateName: getproject?.stateName,
      cityName: getproject?.cityName,
      isMEP: getproject?.isMEP || false,
      isStructural: getproject?.isStructural || false,
      isFireSprinkler: getproject?.isFireSprinkler || false,
      contracts: getproject?.contracts && getproject.contracts.length > 0
        ? getproject.contracts.map((c: IContract) => ({
          _id: c._id,
          proposalName: c.proposalName || '',
          dateOfApproval: c.dateOfApproval ? moment(c.dateOfApproval).format('YYYY-MM-DD') : '',
          attachments: c.attachments || [],
          salesRepresentative: isSalesRepObject(c.salesRepresentative)
            ? c.salesRepresentative._id
            : typeof c.salesRepresentative === 'string'
              ? c.salesRepresentative
              : '',
        }))
        : [{ proposalName: '', dateOfApproval: '', attachments: [], salesRepresentative: '' }],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      clientEmail: Yup.string()
        .email('Invalid email address')
        .required('Client Email is required'),
      blueprint: Yup.string().required('Blueprint is required'),
      quoteNumber: Yup.string().matches(
        /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
        'Only digits are allowed!!'
      ),
      clientContactPerson: Yup.string().required('Client Name is required'),
      folderNumber: Yup.string().required('folderNumber is required'),
      stateName: Yup.string().required('State is required'),
      cityName: Yup.string().required('City is required'),
      isMEP: Yup.boolean(),
      isStructural: Yup.boolean(),
      isFireSprinkler: Yup.boolean().test(
        'at-least-one-project-type',
        'Please select at least one project type',
        function () {
          const { isMEP, isStructural, isFireSprinkler } = this.parent;
          return isMEP || isStructural || isFireSprinkler;
        }
      ),
      contracts: Yup.array().of(
        Yup.object().shape({
          proposalName: Yup.string().required('Proposal Name is required'),
          dateOfApproval: Yup.string().required('Date is required'),
          salesRepresentative: Yup.string().required('Sales Representative is required'),
          attachments: Yup.array().of(Yup.string()),
        })
      ),
    }),
    onSubmit: (values) => {
      const payload = { ...values };
      if (!payload.clientEmailId) {
        delete payload.clientEmailId;
      }

      if (projectId) {
        dispatch(editProjectById(payload, projectId, 'all'));
        navigate('/create-project?step=2&id=' + projectId);
      } else {
        dispatch(createStepOne(payload, formik));
      }
    },
  });
  const { handleSubmit, setFieldValue, handleBlur, touched, values, errors } = formik;

  const handleNextClick = () => {
    handleSubmit();
  };
  // Load all US states
  useEffect(() => {
    const usStates = State.getStatesOfCountry('US');
    setStates(usStates);
    const allStates = State.getStatesOfCountry('US') || [];
    const statesWithCities = allStates.filter((s) => {
      const citiesForState = City.getCitiesOfState('US', s.isoCode) || [];
      return Array.isArray(citiesForState) && citiesForState.length > 0;
    });
    setStates(statesWithCities);
  }, []);

  useEffect(() => {
    const selectedStateName = values.stateName;
    if (!selectedStateName) {
      setCities([]);
      setFieldValue('cityName', '', true);
      return;
    }

    // 🔑 Find ISO code from state name
    const matchedState = states.find(
      (s) => s.name === selectedStateName
    );

    if (!matchedState) {
      setCities([]);
      setFieldValue('cityName', '', true);
      return;
    }

    const stateCode = matchedState.isoCode; // CA, NY, etc.

    const stateCities = City.getCitiesOfState('US', stateCode);
    setCities(stateCities);
  }, [values.stateName, states]);

  useEffect(() => {
    dispatch(getAllUserWithoutPagination());
    dispatch(getAllClients());
  }, [dispatch]);

  useEffect(() => {
    if (contractError) {
      const hasIncomplete = (values.contracts || []).some((c) => {
        return (
          !c.proposalName ||
          !c.dateOfApproval ||
          !c.salesRepresentative
        );
      });
      if (!hasIncomplete) {
        setContractError(null);
      }
    }
  }, [values.contracts, contractError]);

  const handleRowFileChange = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    setUploadingRowIndex(index);
    setUploadErrors(prev => ({ ...prev, [index]: '' }));

    try {
      const currentAttachments = values.contracts?.[index]?.attachments || [];
      const uploadedPaths: string[] = [...currentAttachments];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const extension = file.name.split('.').pop()?.toLowerCase();
        const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
        if (!extension || !allowedExtensions.includes(extension)) {
          setUploadErrors(prev => ({
            ...prev,
            [index]: 'Unsupported format. Allowed: PDF, PNG, JPG, JPEG, DOC, DOCX, XLS, XLSX, PPT, PPTX'
          }));
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'contract');
        formData.append('uploadType', 'File');

        const response = await uploadMedia(formData);
        if (response?.data?.status && response?.data?.data) {
          uploadedPaths.push(response.data.data);
        } else {
          setUploadErrors(prev => ({ ...prev, [index]: 'Failed to upload file' }));
        }
      }
      setFieldValue(`contracts[${index}].attachments`, uploadedPaths, true);
    } catch (err) {
      console.error('File upload error:', err);
      setUploadErrors(prev => ({ ...prev, [index]: 'An error occurred during file upload' }));
    } finally {
      setUploadingRowIndex(null);
      if (event.target) {
        event.target.value = '';
      }
    }
  };
  return (
    <div className='container-fluid'>
      <form onSubmit={handleSubmit}>
        <Box mb={3} sx={{ display: ['block', 'grid', 'grid'], gridTemplateColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'], gap: 3 }}>
          <Box className='mt-2'>
            <Inputform
              label={"Name"}
              Placeholder={"Project Name..."}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              Name={"name"}
              Title_sx={{ color: "grey3" }}
              touched={touched?.name}
              errors={errors?.name}
              values={values.name}
              asterisk={true}
              Max_Length={10}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("name", e.target.value, true)
              }}
            />
          </Box>
          <Box className='mt-2'>
            <FormSelect
              label={"Project Manager"}
              value={values?.projectManager}
              placeholder={"Select Project Manager"}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}

              options={projectManagers.map((projectManager) => ({
                label: `${projectManager?.FirstName} ${projectManager?.LastName}`,
                value: projectManager?._id
              })) || []}
              isMulti={false}
              DefultSelect='Select PM'
              touched={touched?.projectManager}
              errors={errors?.projectManager}
              onBlur={handleBlur}
              Name='projectManager'
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue("projectManager", option ? option.value : '', true);
              }}
            />
          </Box>

          <Box className='mt-2'>
            <FormSelect
              label={"Sales Representative"}
              value={values?.salesRepresentative}
              placeholder={"Select SalesRepresentative"}
              options={salesReps.map((user: usersProps) => ({ label: `${user?.FirstName} ${user?.LastName}`, value: user?._id }))}
              isMulti={false}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              DefultSelect='Select'
              touched={touched?.salesRepresentative}
              errors={errors?.salesRepresentative}
              onBlur={handleBlur}
              Name='salesRepresentative'
              asterisk={true}
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue("salesRepresentative", option ? option.value : '', true);
              }}
            />
          </Box>
          <Box className='mt-2'>
            <FormSelect
              label={"Architect"}
              placeholder='Select Architect'
              value={values?.architect}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}

              options={architects.map((architect) => ({
                label: architect?.name,
                value: architect?._id
              })) || []}
              touched={touched?.architect}
              errors={errors?.architect}
              onBlur={handleBlur}
              Name='architect'
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue("architect", option ? option.value : '', true)
              }}
            />
          </Box>
          <Box className='mt-2'>
            <Inputform
              // mb={4}
              label={"Quote Number"}
              Placeholder={"Quote Number"}
              Name={"quoteNumber"}
              type='text'
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}

              Title_sx={{ color: "grey3" }}
              touched={touched?.quoteNumber}
              errors={errors?.quoteNumber}
              values={values.quoteNumber}
              Max_Length={10}
              onBlur={handleBlur}
              OnChange={(event) => {
                // Filter out non-numeric characters
                const cleanedValue = event.target.value.replace(/[^\d]/g, ''); // Only digits (0-9) allowed
                setFieldValue("quoteNumber", cleanedValue, true);
              }}
              onKeyDown={(event) => {
                const charCode = event.which ? event.which : event.keyCode;
                if (charCode < 48 || charCode > 57) {
                  event.preventDefault();
                }
              }}
            />
          </Box>
          <Box className='mt-2'>
            <FolderNumberDropdown
              label={"Folder Number"}
              value={values?.folderNumber}
              touched={touched?.folderNumber}
              errors={errors?.folderNumber}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}

              onBlur={handleBlur}
              asterisk={true}
              onChange={(selectedFolderNumber) => {
                setFieldValue("folderNumber", selectedFolderNumber, true);
              }}
              disable={false}
            />
          </Box>
          <Box className='mt-2'>
            <Inputform
              label={"Client Address"}
              Placeholder={"Enter Address..."}
              Name={"clientAddress"}
              Title_sx={{ color: "grey3" }}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              touched={touched?.clientAddress}
              errors={errors?.clientAddress}
              values={values.clientAddress}
              asterisk={false}
              Max_Length={10}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("clientAddress", e.target.value, true)
              }}
            />
          </Box>
          <Box className='mt-2'>
            <FormSelect
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              label="Client Email"
              asterisk={true}
              isCreatable={true}
              value={values.clientEmail || null}
              placeholder="Select or enter client email..."
              options={emailOptions}
              isMulti={false}
              DefultSelect="Select client email..."
              touched={touched?.clientEmail}
              errors={errors?.clientEmail}
              Name="clientEmail"
              onChange={(newValue: { label: string; value: string; clientName?: string; clientId?: string } | null) => {
                if (!newValue) {
                  setFieldValue('clientEmail', '', true);
                  setFieldValue('clientEmailId', '', true);
                  setFieldValue('clientContactPerson', '', true);
                  return;
                }

                const updatedEmail = newValue.value;
                const matchedOption = emailOptions.find(opt => opt.value.toLowerCase() === updatedEmail.toLowerCase());

                if (matchedOption) {
                  setFieldValue('clientEmail', matchedOption.value, true);
                  setFieldValue('clientEmailId', matchedOption.clientId || '', true);
                  setFieldValue('clientContactPerson', matchedOption.clientName || '', true);
                } else {
                  setFieldValue('clientEmail', updatedEmail, true);
                  setFieldValue('clientContactPerson', '', true);
                }
              }}
            />
          </Box>
          <Box className='mt-2'>
            <Inputform
              label={"Client Name"}
              asterisk={true}
              Placeholder={"Enter Name..."}
              Name={"clientContactPerson"}
              Title_sx={{ color: "grey3" }}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              touched={touched?.clientContactPerson}
              errors={errors?.clientContactPerson}
              values={values.clientContactPerson}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("clientContactPerson", e.target.value, true);
              }}

            />
          </Box>
          <Box className='mt-2'>
            <FormSelect
              label={"State Name"}
              value={values?.stateName}
              placeholder={"Select State Name"}
              options={states.map((state) => ({
                label: state?.name,
                value: state?.name
              })) || []}
              isMulti={false}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              DefultSelect='Select state name'
              touched={touched?.stateName}
              errors={errors?.stateName}
              onBlur={handleBlur}
              Name='stateName'
              asterisk={true}
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue("stateName", option ? option.value : '', true);
                setFieldValue("cityName", "", true);
                setCities([])
              }}
            />
          </Box>
          <Box className='mt-2'>
            <FormSelect
              label={"City Name"}
              value={values?.cityName}
              placeholder={"Select City Name"}
              options={cities.map((city) => ({
                label: city?.name,
                value: city?.name
              })) || []}
              isMulti={false}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              DefultSelect='Select city name'
              touched={touched?.cityName}
              errors={errors?.cityName}
              onBlur={handleBlur}
              Name='cityName'
              asterisk={true}
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue("cityName", option ? option.value : '', true);
              }}
            />
          </Box>

          <Box className='mt-2'>
            <Inputform
              label={"Site Visit PIN"}
              Placeholder={"Enter Site Visit PIN"}
              sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              Name={"siteVisitPin"}
              Title_sx={{ color: "grey3" }}
              touched={touched?.siteVisitPin}
              errors={errors?.siteVisitPin}
              values={values.siteVisitPin}
              Max_Length={10}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("siteVisitPin", e.target.value, true)
              }}
            />
          </Box>
          <Box className='mt-2' sx={{ alignItems: 'center' }}>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white" }}>T&M Quote</Text>
            <Box sx={{ display: 'flex' }}>
              <Label sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              >
                <Radio
                  name='tmquote'
                  value='true'
                  checked={values.tmQuote === true}
                  onChange={() => setFieldValue('tmQuote', true)}
                  onBlur={handleBlur}
                />
                Yes
              </Label>
              <Label sx={{ color: colorMode === "light" ? "secondary" : "white" }}
              >
                <Radio
                  name='tmquote'
                  value='false'
                  checked={values.tmQuote === false}
                  onChange={() => setFieldValue('tmQuote', false)}
                  onBlur={handleBlur}
                  defaultChecked
                />
                No
              </Label>
            </Box>
            {touched.tmQuote && errors.tmQuote && <p className='text-danger'>{errors.tmQuote}</p>}
          </Box>
          <Box className='mt-2' sx={{ alignItems: 'center' }}>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white" }}>Type of Project<span style={{ color: '#dc3545' }}> *</span></Text>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Label sx={{ color: colorMode === "light" ? "secondary" : "white", width: 'auto', mr: 3 }}>
                <Checkbox
                  checked={values.isMEP}
                  onChange={(e) => setFieldValue('isMEP', e.target.checked)}
                />
                MEP
              </Label>
              <Label sx={{ color: colorMode === "light" ? "secondary" : "white", width: 'auto', mr: 3 }}>
                <Checkbox
                  checked={values.isStructural}
                  onChange={(e) => setFieldValue('isStructural', e.target.checked)}
                />
                Structural
              </Label>
              <Label sx={{ color: colorMode === "light" ? "secondary" : "white", width: 'auto' }}>
                <Checkbox
                  checked={values.isFireSprinkler}
                  onChange={(e) => setFieldValue('isFireSprinkler', e.target.checked)}
                />
                Fire Sprinkler
              </Label>
            </Box>
            {(touched.isMEP || touched.isStructural || touched.isFireSprinkler || formik.submitCount > 0) && errors.isFireSprinkler && (
              <p className='text-danger' style={{ marginTop: '4px', fontSize: '14px' }}>{errors.isFireSprinkler}</p>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: colorMode === "light" ? "#eaeaea" : "#2d3748",
            borderRadius: "12px",
            padding: "12px",
            // my: 3,
            backgroundColor: colorMode === "light" ? "#ffffff" : "#1e2433",
          }}
        >
          <Flex sx={{ justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Text sx={{ fontSize: "18px", fontWeight: 500, color: colorMode === "light" ? "secondary" : "white", letterSpacing: "0.5px" }}>
              Contracts
            </Text>
            <Button
              type="button"
              disabled={uploadingRowIndex !== null}
              onClick={() => {
                const currentContracts = values.contracts || [];
                const incompleteIndex = currentContracts.findIndex((c) => {
                  return (
                    !c.proposalName ||
                    !c.dateOfApproval ||
                    !c.salesRepresentative
                  );
                });

                if (incompleteIndex !== -1) {
                  setContractError(`Please complete the previous row before adding a new contract.`);
                  formik.setFieldTouched(`contracts[${incompleteIndex}].proposalName`, true);
                  formik.setFieldTouched(`contracts[${incompleteIndex}].dateOfApproval`, true);
                  formik.setFieldTouched(`contracts[${incompleteIndex}].salesRepresentative`, true);
                  return;
                }

                setContractError(null);
                const newContracts = [
                  ...currentContracts,
                  { proposalName: "", dateOfApproval: "", attachments: [], salesRepresentative: "" }
                ];
                setFieldValue("contracts", newContracts);
              }}
              sx={{
                background: "transparent",
                color: uploadingRowIndex !== null ? "#c0c0c0" : "#7743DB",
                border: "none",
                fontWeight: 600,
                cursor: uploadingRowIndex !== null ? "not-allowed" : "pointer",
                p: 0,
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": { color: uploadingRowIndex !== null ? "#c0c0c0" : "#5f32b3" }
              }}
            >
              Add More +
            </Button>
          </Flex>

          {contractError && (
            <p className='text-danger' style={{ marginTop: '-12px', marginBottom: '16px', fontSize: '14px', fontWeight: 500 }}>
              {contractError}
            </p>
          )}

          {/* Table Headers */}
          <Box
            sx={{
              display: ["none", "none", "grid"],
              gridTemplateColumns: "2.5fr 1.8fr 2.2fr 2.5fr 0.5fr",
              gap: 3,
              pb: 1,
              mb: 2,
            }}
          >
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white", lineHeight: "normal" }}>Proposal Name <span style={{ color: "#dc3545" }}>*</span></Text>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white", lineHeight: "normal" }}>Date of Approval <span style={{ color: "#dc3545" }}>*</span></Text>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white", lineHeight: "normal" }}>Sales Representative <span style={{ color: "#dc3545" }}>*</span></Text>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white", lineHeight: "normal" }}>Signed Contract</Text>
            <Text variant='SelectLabel' sx={{ color: colorMode === "light" ? "secondary" : "white", lineHeight: "normal", textAlign: "center" }}>Action</Text>
          </Box>

          {/* Table Rows */}
          <Flex sx={{ flexDirection: "column", gap: [4, 4, 3] }}>
            {(values.contracts || []).map((contract, index) => {
              const rowErrors = (errors.contracts as FormikErrors<IContract>[])?.[index] || {};
              const rowTouched = (touched.contracts as FormikTouched<IContract>[])?.[index] || {};

              return (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: ["1fr", "1fr", "2.5fr 1.8fr 2.2fr 2.5fr 0.5fr"],
                    gap: 3,
                    alignItems: "start",
                    pb: [3, 3, 0],
                    borderBottom: ["1px solid", "1px solid", "none"],
                    borderColor: colorMode === "light" ? "#eaeaea" : "#2d3748",
                  }}
                >
                  {/* Proposal Name */}
                  <Box>
                    <Text sx={{ display: ["block", "block", "none"], fontSize: "12px", fontWeight: 600, color: "grey3", mb: 1 }}>Proposal Name</Text>
                    <Inputform
                      Placeholder="Proposal Name..."
                      Name={`contracts[${index}].proposalName`}
                      touched={rowTouched.proposalName}
                      errors={rowErrors.proposalName}
                      values={contract.proposalName}
                      onBlur={handleBlur}
                      OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue(`contracts[${index}].proposalName`, e.target.value, true);
                      }}
                    />
                  </Box>

                  {/* Date of Approval */}
                  <Box>
                    <Text sx={{ display: ["block", "block", "none"], fontSize: "12px", fontWeight: 600, color: "grey3", mb: 1 }}>Date of Approval</Text>
                    <Inputform
                      inputClassName="custom-datepicker-input"
                      wrapperClassName="date-input-wrapper"
                      type="date"
                      Placeholder="mm-dd-yyyy"
                      dateFormat="MM-dd-yyyy"
                      Name={`contracts[${index}].dateOfApproval`}
                      touched={rowTouched.dateOfApproval}
                      errors={rowErrors.dateOfApproval}
                      values={contract.dateOfApproval}
                      onBlur={handleBlur}
                      OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue(`contracts[${index}].dateOfApproval`, e.target.value, true);
                      }}
                    />
                  </Box>

                  {/* Sales Representative */}
                  <Box>
                    <Text sx={{ display: ["block", "block", "none"], fontSize: "12px", fontWeight: 600, color: "grey3", mb: 1 }}>Sales Representative</Text>
                    <FormSelect
                      value={contract.salesRepresentative}
                      placeholder="Select Representative"
                      options={salesReps.map((rep: { FirstName?: string; LastName?: string; _id?: string }) => ({
                        label: `${rep.FirstName || ''} ${rep.LastName || ''}`.trim(),
                        value: rep._id || '',
                      }))}
                      isMulti={false}
                      touched={rowTouched.salesRepresentative}
                      errors={rowErrors.salesRepresentative}
                      onBlur={handleBlur}
                      Name={`contracts[${index}].salesRepresentative`}
                      onChange={(option: { label: string; value: string } | null) => {
                        setFieldValue(`contracts[${index}].salesRepresentative`, option ? option.value : "", true);
                      }}
                    />
                  </Box>

                  {/* Signed Contract Upload */}
                  <Box>
                    <Text sx={{ display: ["block", "block", "none"], fontSize: "12px", fontWeight: 600, color: "grey3", mb: 1 }}>Signed Contract</Text>
                    <input
                      type="file"
                      multiple
                      disabled={uploadingRowIndex !== null}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      style={{ display: "none" }}
                      id={`contract-file-upload-${index}`}
                      onChange={(e) => handleRowFileChange(e, index)}
                    />
                    <label htmlFor={uploadingRowIndex !== null ? undefined : `contract-file-upload-${index}`} style={{ display: "block" }}>
                      <Box
                        sx={{
                          border: "2px dashed",
                          borderColor: rowErrors.attachments && rowTouched.attachments ? "#dc3545" : "#D3D5D9",
                          borderRadius: "8px",
                          padding: "12px",
                          textAlign: "center",
                          cursor: uploadingRowIndex !== null ? "not-allowed" : "pointer",
                          backgroundColor: colorMode === "light" ? "#fcfcfc" : "#1e1b2f",
                          transition: "border-color 0.2s",
                          "&:hover": {
                            borderColor: uploadingRowIndex !== null ? "#D3D5D9" : "#7743DB",
                          },
                        }}
                      >
                        {uploadingRowIndex === index ? (
                          <Flex sx={{ justifyContent: "center", alignItems: "center", gap: 2 }}>
                            <Spinner size={16} />
                            <Text sx={{ fontSize: "12px", fontWeight: 500 }}>Uploading...</Text>
                          </Flex>
                        ) : (
                          <Text sx={{ fontSize: "12px", color: "#6c757d" }}>
                            Click to upload contract file
                          </Text>
                        )}
                      </Box>
                    </label>

                    {uploadErrors[index] && (
                      <Text sx={{ color: "red", fontSize: "11px", mt: 1, display: "block" }}>
                        {uploadErrors[index]}
                      </Text>
                    )}



                    {contract.attachments && contract.attachments.length > 0 && (
                      <Box mt={2}>
                        <Flex sx={{ flexDirection: "column", gap: 1 }}>
                          {contract.attachments.map((file, idx) => (
                            <Flex
                              key={idx}
                              sx={{
                                alignItems: "center",
                                padding: "4px 8px",
                                border: "1px solid #eaeaea",
                                borderRadius: "6px",
                                backgroundColor: colorMode === "light" ? "#f8f9fa" : "#282b3d",
                              }}
                            >
                              <Text
                                sx={{
                                  fontSize: "11px",
                                  color: colorMode === "light" ? "#495057" : "white",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.split("/").pop() || file}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </Box>

                  {/* Action Delete Button */}
                  <Flex sx={{ justifyContent: "center", alignItems: "center", height: ["auto", "auto", "38px"] }}>
                    {values.contracts.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => {
                          const newContracts = values.contracts.filter((_, idx) => idx !== index);
                          setFieldValue("contracts", newContracts);
                        }}
                        sx={{
                          background: "transparent",
                          color: "#dc3545",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          width: "32px",
                          height: "32px",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: "#dc3545",
                          fontSize: "18px",
                          fontWeight: "bold",
                          lineHeight: "1",
                          "&:hover": {
                            background: "#fff5f5",
                          }
                        }}
                      >
                        &minus;
                      </Button>
                    )}
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        </Box>

        <MultiCard
          value={values.blueprint}
          onChange={(blueprint: string) => formik.setFieldValue("blueprint", blueprint)}
          labelledBy="Select Blueprint Template"
        />
        {touched.blueprint && errors.blueprint && <p className="text-danger">{errors?.blueprint}</p>}
      </form>
      <Flex sx={{ justifyContent: "end", paddingBottom: "30px" }}>
        <Button disabled={loader} sx={{ background: theme.colors?.primary, padding: "12px 18px", width: "25%" }} onClick={handleNextClick}> {loader ? <Spinner size={30} sx={{ color: "#fff" }} /> : "Next"}</Button>
      </Flex>
    </div>
  );
};

export default StepOne;
